import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportJobCostByProductType from "hooks/organization/reports/types/useGetReportJobCostByProductType";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableJobCostByProductType(startDate, endDate, jobId = null) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportJobCostByProductType, { data, loading, error }] =
    useGetReportJobCostByProductType(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        jobId,
        page,
        first,
      };
      getReportJobCostByProductType({ variables });
    },
    [startDate, endDate, jobId, getReportJobCostByProductType]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportJobCostByProductType?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Job ID",
        accessor: "job_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job Size",
        accessor: "job_size",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Product Type",
        accessor: "organization_product_type_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Labor Hours",
        accessor: "proposed_employee_hours",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Actual Labor Hours",
        accessor: "actual_employee_hours",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Material Cost",
        accessor: "proposed_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Material Cost",
        accessor: "actual_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Labor Cost",
        accessor: "proposed_cost_labour",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Labor Cost",
        accessor: "actual_cost_labour",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Trucking Cost",
        accessor: "proposed_cost_transfer_truck",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Trucking Cost",
        accessor: "actual_cost_transfer_truck",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Equipment Cost",
        accessor: "proposed_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Equipment Cost",
        accessor: "actual_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposed Subcontractor Cost",
        accessor: "proposed_cost_subcontractor",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" }),
        disableSortBy: true,
      },
      {
        Header: "Actual Subcontractor Cost",
        accessor: "actual_cost_subcontractor",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" }),
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportJobCostByProductType?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportJobCostByProductType: paginate,
  } as const;
}
